import React, { useState, useEffect, useCallback } from 'react';
import { Info } from 'lucide-react';
import { hasPI } from '../../../lib/permissions';
import { piApi } from '../../../services/piApi';
import '../PIShell.css';

// ── IRR calculation (Newton-Raphson) ─────────────────────────────────────────
function calcIRR(cost, proceeds, years) {
  if (!cost || !proceeds || !years || years <= 0) return null;
  let r = 0.15; // initial guess
  for (let i = 0; i < 200; i++) {
    const f  = cost - proceeds / Math.pow(1 + r, years);
    const df = proceeds * years / Math.pow(1 + r, years + 1);
    const next = r - f / df;
    if (Math.abs(next - r) < 0.0001) return Math.round(next * 1000) / 10; // % with 1dp
    r = next;
  }
  return Math.round(r * 1000) / 10;
}

function calcMOIC(cost, exitEbitda, exitMultiple, debtAtExit) {
  if (!cost || !exitEbitda || !exitMultiple) return null;
  const equityAtExit = exitEbitda * exitMultiple - (debtAtExit || 0);
  return Math.round((equityAtExit / cost) * 100) / 100;
}

function calcHoldYears(closeDate, exitYear) {
  if (!closeDate || !exitYear) return null;
  const closeYr = new Date(closeDate).getFullYear();
  return exitYear - closeYr;
}

const SCENARIO_TABS = ['base', 'bull', 'bear'];
const EXIT_MULTIPLES = [4, 5, 6, 7, 8];
const EXIT_EBITDA_OFFSETS = [-20, -10, 0, 10, 20]; // % offsets from base

function ragIRR(irr, target) {
  if (!irr || !target) return '';
  if (irr >= target) return 'pi-badge-green';
  if (irr >= target - 2) return 'pi-badge-amber';
  return 'pi-badge-red';
}

export default function PIReturnsTab({ portco, currentUser }) {
  const [scenarios, setScenarios] = useState({ base: {}, bull: {}, bear: {} });
  const [activeScenario, setActiveScenario] = useState('base');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saveConfirm, setSaveConfirm] = useState(false);

  const canEdit = hasPI(currentUser, 'pi.returns.edit');

  useEffect(() => {
    piApi.getReturnScenarios(portco.id).then(r => {
      if (!r.data) return;
      const d = r.data;
      if (Array.isArray(d)) {
        const map = {};
        d.forEach(s => { map[s.scenario] = s; });
        setScenarios(s => ({ ...s, ...map }));
      } else if (typeof d === 'object') {
        setScenarios(s => ({ ...s, ...d }));
      }
    }).catch(() => {});
  }, [portco.id]);

  const current = scenarios[activeScenario] || {};
  const vcpContribution = portco.vcp_ebitda_contribution || 0;
  const adjustedExitEbitda = (current.exit_ebitda || 0) + vcpContribution;
  const holdYears = calcHoldYears(portco.close_date, current.exit_year);
  const irr  = calcIRR(portco.acquisition_price, adjustedExitEbitda * (current.exit_multiple || 0) - (current.debt_at_exit || 0), holdYears);
  const moic = calcMOIC(portco.acquisition_price, adjustedExitEbitda, current.exit_multiple, current.debt_at_exit);

  const [draft, setDraft] = useState({});

  useEffect(() => {
    const c = scenarios[activeScenario] || {};
    setDraft({
      exit_ebitda: c.exit_ebitda ?? '',
      exit_multiple: c.exit_multiple ?? '',
      exit_year: c.exit_year ?? '',
      debt_at_exit: c.debt_at_exit ?? '',
    });
  }, [activeScenario, scenarios]);

  const updateDraft = (field, raw) => {
    setDraft(d => ({ ...d, [field]: raw }));
    const num = raw === '' ? null : parseFloat(raw);
    if (raw !== '' && isNaN(num)) return;
    setScenarios(s => ({ ...s, [activeScenario]: { ...s[activeScenario], [field]: num } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await piApi.saveReturnScenario(portco.id, activeScenario, {
        ...scenarios[activeScenario],
        projected_irr:  irr,
        projected_moic: moic,
      });
      setSaveConfirm(true);
      setTimeout(() => setSaveConfirm(false), 3000);
    } catch (e) { console.error('[Returns] Save error:', e); }
    finally { setSaving(false); }
  };

  // 5×5 sensitivity grid — uses adjusted EBITDA (base + VCP contribution) as center
  const baseEbitdaRaw = scenarios.base?.exit_ebitda || portco.entry_ebitda || 10;
  const adjustedCenter = baseEbitdaRaw + vcpContribution;
  const sensitivityGrid = EXIT_MULTIPLES.map(mult =>
    EXIT_EBITDA_OFFSETS.map(offset => {
      const ebitda = adjustedCenter * (1 + offset / 100);
      const equity = ebitda * mult - (scenarios.base?.debt_at_exit || 0);
      const holdYrs = calcHoldYears(portco.close_date, scenarios.base?.exit_year) || portco.target_hold_years || 5;
      return calcIRR(portco.acquisition_price, equity, holdYrs);
    })
  );

  const renderInput = (label, field, suffix) => (
    <div style={{ marginBottom: 12 }} key={field}>
      <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          type="number" value={draft[field] ?? ''} disabled={!canEdit}
          onChange={e => updateDraft(field, e.target.value)}
          style={{
            flex: 1, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6,
            fontSize: 13, background: canEdit ? 'white' : '#f8fafc', cursor: canEdit ? 'text' : 'default',
          }}
        />
        {suffix && <span style={{ fontSize: 12, color: '#94a3b8' }}>{suffix}</span>}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>

        {/* LEFT: Inputs */}
        <div style={{ flex: '0 0 300px' }}>
          <div className="pi-card">
            <div className="pi-card-header">
              <span className="pi-card-title">Exit Scenarios</span>
            </div>
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 6 }}>
              {SCENARIO_TABS.map(s => {
                const subtitle = { base: 'Your expected case', bull: 'Upside scenario', bear: 'Downside scenario' }[s];
                return (
                  <button key={s} onClick={() => setActiveScenario(s)}
                    style={{
                      flex: 1, padding: '6px 0', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', transition: 'all 120ms',
                      background: activeScenario === s ? '#eef2ff' : '#f8fafc',
                      color: activeScenario === s ? '#4f46e5' : '#64748b',
                    }}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                    {activeScenario === s && (
                      <div style={{ fontSize: 10, fontWeight: 400, color: '#94a3b8', marginTop: 2 }}>{subtitle}</div>
                    )}
                  </button>
                );
              })}
            </div>
            <div style={{ padding: '16px' }}>
              {!editing ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                    <div>
                      <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>EXIT EBITDA</span>
                      <span style={{ fontSize: 14, color: '#1e293b', fontWeight: 600 }}>{current.exit_ebitda != null ? `$${current.exit_ebitda}M` : '—'}</span>
                    </div>
                    <div>
                      <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>EXIT MULTIPLE</span>
                      <span style={{ fontSize: 14, color: '#1e293b', fontWeight: 600 }}>{current.exit_multiple != null ? `${current.exit_multiple}x` : '—'}</span>
                    </div>
                    {vcpContribution > 0 && (
                      <>
                        <div style={{ gridColumn: '1 / -1', borderTop: '1px dashed #e2e8f0', paddingTop: 8, marginTop: 2 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
                            <span style={{ fontSize: 10, color: '#4f46e5', fontWeight: 600 }}>+ VCP CONTRIBUTION</span>
                            <span title="Computed from VCP initiatives: sum of realized actual impacts plus 50% credit for in-progress initiatives. Edit initiatives in the Value Creation tab to update this figure." style={{ cursor: 'help' }}>
                              <Info size={11} color="#94a3b8" />
                            </span>
                          </div>
                          <span style={{ fontSize: 14, color: '#4f46e5', fontWeight: 600 }}>${vcpContribution}M</span>
                        </div>
                        <div style={{ gridColumn: '1 / -1', background: '#eef2ff', borderRadius: 6, padding: '6px 10px' }}>
                          <span style={{ fontSize: 10, color: '#4f46e5', fontWeight: 600, display: 'block', marginBottom: 2 }}>= ADJUSTED EXIT EBITDA</span>
                          <span style={{ fontSize: 16, color: '#1e293b', fontWeight: 700 }}>${adjustedExitEbitda}M</span>
                          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>Based on your inputs + VCP progress</div>
                        </div>
                      </>
                    )}
                    <div>
                      <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>EXIT YEAR</span>
                      <span style={{ fontSize: 14, color: '#1e293b', fontWeight: 600 }}>{current.exit_year ?? '—'}</span>
                    </div>
                    <div>
                      <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>DEBT AT EXIT</span>
                      <span style={{ fontSize: 14, color: '#1e293b', fontWeight: 600 }}>{current.debt_at_exit != null ? `$${current.debt_at_exit}M` : '—'}</span>
                    </div>
                  </div>
                  {current.notes && (
                    <div style={{ marginBottom: 14 }}>
                      <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 3 }}>NOTES</span>
                      <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.45, margin: 0 }}>{current.notes}</p>
                    </div>
                  )}
                  {canEdit && (
                    <button className="pi-btn-primary" style={{ width: '100%' }} onClick={() => setEditing(true)}>
                      Edit Scenario
                    </button>
                  )}
                </>
              ) : (
                <>
                  {renderInput("EXIT EBITDA ($M)", "exit_ebitda", "$M")}
                  {vcpContribution > 0 && (
                    <div style={{ marginBottom: 12, padding: '8px 10px', background: '#eef2ff', borderRadius: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: '#4f46e5', fontWeight: 600 }}>+ VCP Contribution: ${vcpContribution}M</span>
                        <Info size={11} color="#94a3b8" />
                      </div>
                      <span style={{ fontSize: 13, color: '#1e293b', fontWeight: 700 }}>= Adjusted Exit EBITDA: ${adjustedExitEbitda}M</span>
                    </div>
                  )}
                  {renderInput("EXIT MULTIPLE", "exit_multiple", "x")}
                  {renderInput("EXIT YEAR", "exit_year")}
                  {renderInput("DEBT AT EXIT ($M)", "debt_at_exit", "$M")}
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button className="pi-btn-ghost" style={{ flex: 1 }} onClick={() => setEditing(false)}>Cancel</button>
                    <button className="pi-btn-primary" style={{ flex: 1 }}
                      onClick={async () => { await handleSave(); setEditing(false); }} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Scenario'}
                    </button>
                  </div>
                  {saveConfirm && (
                    <div style={{ fontSize: 12, color: '#16a34a', marginTop: 6, textAlign: 'center', transition: 'opacity 300ms' }}>
                      Scenario saved. All values reflect your inputs.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Live output */}
        <div style={{ flex: 1 }}>
          <div className="pi-card" style={{ marginBottom: 14 }}>
            <div className="pi-card-header"><span className="pi-card-title">Exit Scenarios — {activeScenario.charAt(0).toUpperCase() + activeScenario.slice(1)}</span></div>
            <div style={{ padding: '20px 24px', display: 'flex', gap: 32 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 36, fontWeight: 700, color: irr != null && portco.target_irr && irr >= portco.target_irr ? '#16a34a' : '#dc2626' }}>
                  {irr != null ? `${irr}%` : '—'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 4 }}>
                  <span style={{ fontSize: 12, color: '#64748b' }}>Levered IRR</span>
                  <span title="IRR is calculated using your entered exit equity proceeds and hold period. All inputs are yours." style={{ cursor: 'help' }}>
                    <Info size={12} color="#94a3b8" />
                  </span>
                </div>
                {irr != null && portco.target_irr && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Target: {portco.target_irr}%</div>
                )}
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Based on your inputs</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 36, fontWeight: 700, color: moic != null && portco.target_moic && moic >= portco.target_moic ? '#16a34a' : '#dc2626' }}>
                  {moic != null ? `${moic}x` : '—'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 4 }}>
                  <span style={{ fontSize: 12, color: '#64748b' }}>MOIC</span>
                  <span title="MOIC = Exit Equity ÷ Acquisition Price. Exit Equity = (Exit EBITDA × Exit Multiple) − Debt at Exit. All inputs are yours." style={{ cursor: 'help' }}>
                    <Info size={12} color="#94a3b8" />
                  </span>
                </div>
                {moic != null && portco.target_moic && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Target: {portco.target_moic}x</div>
                )}
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Based on your inputs</div>
              </div>
            </div>
          </div>

          {/* Scenario comparison table */}
          <div className="pi-card" style={{ marginBottom: 14 }}>
            <div className="pi-card-header"><span className="pi-card-title">Scenario Comparison</span></div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: '#fafbff', borderBottom: '1px solid #f1f5f9' }}>
                  {['', 'Base', 'Bull', 'Bear'].map(h => (
                    <th key={h} style={{ padding: '8px 14px', textAlign: h === '' ? 'left' : 'center', color: '#64748b', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {['IRR', 'MOIC', 'Exit Equity'].map(metric => (
                  <tr key={metric} style={{ borderBottom: '1px solid #f8fafc' }}>
                    <td style={{ padding: '9px 14px', color: '#475569', fontWeight: 500 }}>{metric}</td>
                    {SCENARIO_TABS.map(s => {
                      const sc = scenarios[s] || {};
                      const adjEbitda = (sc.exit_ebitda || 0) + vcpContribution;
                      const hy = calcHoldYears(portco.close_date, sc.exit_year) || portco.target_hold_years || 5;
                      const eq = adjEbitda * (sc.exit_multiple || 0) - (sc.debt_at_exit || 0);
                      const v = metric === 'IRR' ? calcIRR(portco.acquisition_price, eq, hy)
                        : metric === 'MOIC' ? calcMOIC(portco.acquisition_price, adjEbitda, sc.exit_multiple, sc.debt_at_exit)
                        : eq ? `$${eq.toFixed(0)}M` : null;
                      return (
                        <td key={s} style={{ padding: '9px 14px', textAlign: 'center', fontWeight: 600, color: '#334155' }}>
                          {metric === 'IRR' && v != null ? (
                            <span className={`pi-badge ${ragIRR(v, portco.target_irr)}`}>{v}%</span>
                          ) : metric === 'MOIC' && v != null ? `${v}x`
                            : v ?? '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 5×5 Sensitivity grid */}
          <div className="pi-card">
            <div className="pi-card-header">
              <span className="pi-card-title">Sensitivity: IRR by Exit Multiple × EBITDA</span>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>
                {vcpContribution > 0
                  ? `Base $${baseEbitdaRaw}M + VCP $${vcpContribution}M = Center $${adjustedCenter}M`
                  : 'Base EBITDA ±% vs Multiple'}
              </span>
            </div>
            <div style={{ overflowX: 'auto', padding: '0 16px 16px' }}>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, marginBottom: 6 }}>All values based on your exit multiple and EBITDA inputs.</div>
              <table style={{ borderCollapse: 'collapse', fontSize: 11.5 }}>
                <thead>
                  <tr>
                    <th style={{ padding: '6px 12px', color: '#64748b', fontWeight: 500, textAlign: 'right' }}>Multiple ↓ / EBITDA →</th>
                    {EXIT_EBITDA_OFFSETS.map(o => (
                      <th key={o} style={{ padding: '6px 12px', color: '#64748b', fontWeight: 500, textAlign: 'center' }}>{o >= 0 ? '+' : ''}{o}%</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {EXIT_MULTIPLES.map((mult, mi) => (
                    <tr key={mult}>
                      <td style={{ padding: '6px 12px', color: '#475569', fontWeight: 600, textAlign: 'right' }}>{mult}x</td>
                      {sensitivityGrid[mi].map((irr, oi) => {
                        const cls = irr == null ? '' : irr >= (portco.target_irr || 20) ? '#dcfce7' : irr >= (portco.target_irr || 20) - 2 ? '#fef3c7' : '#fee2e2';
                        const textColor = irr == null ? '#94a3b8' : irr >= (portco.target_irr || 20) ? '#16a34a' : irr >= (portco.target_irr || 20) - 2 ? '#d97706' : '#dc2626';
                        return (
                          <td key={oi} style={{ padding: '6px 12px', textAlign: 'center', background: cls, borderRadius: 4, fontWeight: 600, color: textColor }}
                            aria-label={`${mult}x multiple, ${EXIT_EBITDA_OFFSETS[oi]}% EBITDA: ${irr != null ? irr + '%' : 'N/A'} IRR`}>
                            {irr != null ? `${irr}%` : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
